import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { Plus, X } from "react-feather";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import styles from "./editrequest.module.css";
import { apiErrorMessage } from "./vehicleRequestUtils";
import { formatUsPhone } from "../public/publicVehicleApi";
import { compressImage } from "../public/imageCompress";

const VALID_TYPES = ["image/jpeg", "image/png"];
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

/*
 * Edit a rejected new vehicle / guest from the Vehicles page. Details and photos are corrected; the request stays
 * rejected and nobody is notified. requestId is the vehicle request id (row.id of a kind "request" row).
 */
const EditRequestDialog = ({ requestId, onClose, onSaved, notifySuccess }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [isGuest, setIsGuest] = useState(false);
  const [values, setValues] = useState({ license: "", make: "", model: "", color: "", guest_fname: "", guest_lname: "", guest_phone: "" });
  const [kept, setKept] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [limits, setLimits] = useState({ min: 1, max: 6 });

  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(() => () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview)), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        const response = await axios.get(process.env.REACT_APP_TENANT_API_URL + "v1/vehicle-requests/" + requestId, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const data = response?.data?.data || {};
        setIsGuest(data.type === "GUEST");
        setValues({
          license: data.license || "",
          make: data.make || "",
          model: data.model || "",
          color: data.color || "",
          guest_fname: data.guest_fname || "",
          guest_lname: data.guest_lname || "",
          guest_phone: formatUsPhone(data.guest_phone || ""),
        });
        setKept(Array.isArray(data.photo_items) ? data.photo_items : []);
        setLimits({ min: data.min_photos || 1, max: data.max_photos || 6 });
      } catch (err) {
        console.error("[Vehicles] Failed to load rejected vehicle", err);
        if (!cancelled) setError(apiErrorMessage(err, "Unable to load the vehicle. Please close and try again."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [requestId, getAccessTokenSilently]);

  const setField = (key) => (e) => {
    const value = key === "guest_phone" ? formatUsPhone(e.target.value) : key === "license" ? e.target.value.toUpperCase() : e.target.value;
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const total = kept.length + photos.length;

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setError("");
    setProcessing(true);
    const accepted = [];
    let message = "";
    for (const file of files) {
      if (VALID_TYPES.indexOf(file.type) === -1) {
        message = "Photos must be JPG or PNG images.";
        continue;
      }
      const compressed = await compressImage(file);
      if (compressed.size > MAX_PHOTO_BYTES) {
        message = "This photo is too large. Please try another photo.";
        continue;
      }
      accepted.push({ file: compressed, preview: URL.createObjectURL(compressed) });
    }
    setProcessing(false);

    const room = Math.max(0, limits.max - kept.length - photosRef.current.length);
    if (accepted.length > room) {
      message = `You can have up to ${limits.max} photos.`;
      accepted.slice(room).forEach((photo) => URL.revokeObjectURL(photo.preview));
    }
    setPhotos((prev) => prev.concat(accepted.slice(0, room)));
    if (message) setError(message);
  };

  const removeNew = (index) => {
    URL.revokeObjectURL(photos[index].preview);
    setPhotos(photos.filter((photo, i) => i !== index));
  };

  const validate = () => {
    if (values.license.trim() === "") return "Please enter the vehicle number.";
    if (values.make.trim() === "") return "Please enter the make.";
    if (values.model.trim() === "") return "Please enter the model.";
    if (values.color.trim() === "") return "Please enter the color.";
    if (isGuest && values.guest_fname.trim() === "") return "Please enter the guest's first name.";
    if (isGuest && values.guest_phone.replace(/\D/g, "").length !== 10) return "Please enter a valid 10-digit phone number.";
    if (total < limits.min) return limits.min === 1 ? "Please keep or add a photo of the vehicle." : `Please keep or add at least ${limits.min} photos.`;
    return "";
  };

  const save = async () => {
    const message = validate();
    if (message) {
      setError(message);
      return;
    }

    const formData = new FormData();
    formData.append("license", values.license.trim());
    formData.append("make", values.make.trim());
    formData.append("model", values.model.trim());
    formData.append("color", values.color.trim());
    if (isGuest) {
      formData.append("guest_fname", values.guest_fname.trim());
      formData.append("guest_lname", values.guest_lname.trim());
      formData.append("guest_phone", values.guest_phone.replace(/\D/g, ""));
    }
    kept.forEach((photo) => formData.append("keep_photos[]", photo.id));
    photos.forEach((photo) => formData.append("photos[]", photo.file));

    setSaving(true);
    setError("");
    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      const response = await axios.post(process.env.REACT_APP_TENANT_API_URL + "v1/vehicle-requests/" + requestId + "/update", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (notifySuccess) notifySuccess(response?.data?.message || "Vehicle details updated.");
      if (onSaved) onSaved();
    } catch (err) {
      console.error("[Vehicles] Failed to update rejected vehicle", err);
      setError(apiErrorMessage(err, "Unable to update the vehicle. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || processing || loading;

  return (<Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" className="NewPopUp MobileForm">
    <DialogTitle>
      Edit rejected vehicle
      <button type="button" onClick={onClose} disabled={saving}><X /></button>
    </DialogTitle>
    <DialogContent className={styles.dialogBody}>
      {loading ? (<div style={{ textAlign: "center", padding: 24 }}><CircularProgress size={28} /></div>) : (<>
        <p className={styles.mutedText}>Correct the details below. The vehicle stays rejected and nobody is notified; the {isGuest ? "tenant or guest" : "tenant"} can still resubmit.</p>

        {isGuest && (<>
          <p className={styles.sectionLabel}>GUEST</p>
          <div className={styles.formRow}>
            <TextField className={styles.formField} label="First name*" value={values.guest_fname} onChange={setField("guest_fname")} inputProps={{ maxLength: 50 }} disabled={saving} />
            <TextField className={styles.formField} label="Last name" value={values.guest_lname} onChange={setField("guest_lname")} inputProps={{ maxLength: 50 }} disabled={saving} />
          </div>
          <div className={styles.formRow}>
            <TextField className={styles.formField} label="Phone*" value={values.guest_phone} onChange={setField("guest_phone")} inputProps={{ maxLength: 14, inputMode: "numeric" }} placeholder="(555) 555-0123" disabled={saving} />
          </div>
        </>)}

        <p className={styles.sectionLabel}>VEHICLE</p>
        <div className={styles.formRow}>
          <TextField className={styles.formField} label="Vehicle number*" value={values.license} onChange={setField("license")} inputProps={{ maxLength: 20 }} disabled={saving} />
          <TextField className={styles.formField} label="Color*" value={values.color} onChange={setField("color")} inputProps={{ maxLength: 30 }} disabled={saving} />
        </div>
        <div className={styles.formRow}>
          <TextField className={styles.formField} label="Make*" value={values.make} onChange={setField("make")} inputProps={{ maxLength: 50 }} disabled={saving} />
          <TextField className={styles.formField} label="Model*" value={values.model} onChange={setField("model")} inputProps={{ maxLength: 50 }} disabled={saving} />
        </div>

        <p className={styles.sectionLabel}>PHOTOS ({total} of {limits.max})</p>
        <div className={styles.photoGrid}>
          {kept.map((photo, index) => (<div key={photo.id} className={styles.photoThumb}>
            <img src={photo.url} alt={`Vehicle photo ${index + 1}`} />
            <button type="button" aria-label="Remove photo" disabled={saving} onClick={() => setKept((prev) => prev.filter((item) => item.id !== photo.id))}>×</button>
          </div>))}
          {photos.map((photo, index) => (<div key={photo.preview} className={styles.photoThumb}>
            <img src={photo.preview} alt={`New vehicle photo ${index + 1}`} />
            <button type="button" aria-label="Remove photo" disabled={saving} onClick={() => removeNew(index)}>×</button>
          </div>))}
          {total < limits.max && (<label className={styles.photoAdd}>
            <Plus size={20} />
            <span>{processing ? "Preparing..." : "Add photo"}</span>
            <input type="file" accept="image/jpeg,image/png" multiple onChange={addPhotos} disabled={busy} />
          </label>)}
        </div>
        <p className={styles.hint}>JPG or PNG · at least {limits.min}, up to {limits.max} photos. Large photos are resized automatically.</p>
      </>)}

      {error !== "" && (<Alert severity="error" className={styles.inlineAlert}>{error}</Alert>)}
    </DialogContent>
    <DialogActions>
      <Button className="CancelPopupBU" disabled={saving} onClick={onClose}>Cancel</Button>
      <Button className="SubmitPopupBU" disabled={busy} onClick={save}>{saving ? "Saving..." : "Save changes"}</Button>
    </DialogActions>
  </Dialog>);
};

export default EditRequestDialog;
